import React from 'react'
import { SiesaItem, getItemPrices, getItemExtensions } from '@/services/OrderAssemblyService'

/**
 * Populate the item form with a selected Siesa item.
 * - Sets form fields (reference, extension, warehouse, measure_unit, notes)
 * - Fetches prices and extensions and stores them into the form
 * - Dispatches DOM events 'siesa:availablePrices' and 'siesa:extensions' for listeners
 */
export async function applySiesaItemToForm(
  item: SiesaItem,
  form: any,
  referenceInputRef?: React.MutableRefObject<any>,
  options?: { defaultWarehouse?: string | null },
) {
  if (!item || !form) return

  try {
    // Basic fields
    const reference = item.reference || ''
    const firstExt = Array.isArray(item.extensions) && item.extensions.length ? item.extensions[0] : undefined

    // IMPORTANT: backend expects the warehouse code/id, not the human description.
    // Keep a very conservative fallback to description only if id is missing.
    // La bodega del pedido manda sobre la que trae el ítem: quien arma el pedido
    // la fija una vez en el paso de información y todas las líneas salen de ahí.
    // Si no hay bodega de pedido, se usa la que el ítem tiene asignada en SIESA.
    const itemWarehouse = (item.warehouse_id ?? item.warehouse_description) ?? undefined
    const warehouse = String(options?.defaultWarehouse || '').trim() || itemWarehouse

    form.setFieldsValue({
      reference,
      item_description: item.description || undefined,
      item_short_description: item.description || undefined,
      // Referencia "corta"/externa de SIESA (f120_descripcion_corta): es la que
      // reconoce el cliente y va en su propia columna del documento, así que se
      // guarda aparte y NO se mezcla con la descripción del ítem.
      item_short_reference: item.short_description || undefined,
      extension: firstExt,
      warehouse,
      // La ubicación pertenece a una bodega: al cambiar de ítem se descarta.
      location: undefined,
      measure_unit: item.measure_unit || undefined,
      notes: item.notes || undefined,
    })

    // If refs are attached to underlying inputs, set their DOM value and trigger input events
    try {
      const setInputValue = (el: any, val: any) => {
        if (!el) return
        try {
          if (typeof el.value !== 'undefined') {
            el.value = val ?? ''
            // trigger input/update events so any listeners react
            try {
              el.dispatchEvent?.(new Event('input', { bubbles: true }))
            } catch {}
          }
        } catch {}
      }
      setInputValue(referenceInputRef?.current, reference)
    } catch (e) {
      // ignore DOM-setting errors
    }

    // Fetch prices and extensions in parallel
    const [prices, exts] = await Promise.all([
      (async () => {
        try {
          return await getItemPrices(reference)
        } catch (e) {
          return []
        }
      })(),
      (async () => {
        try {
          return await getItemExtensions(reference)
        } catch (e) {
          return []
        }
      })(),
    ])

    // Populate the form with fetched details so consumers using Form.useWatch can pick them
    try {
      const normalizedExts = Array.isArray(exts) ? exts : []
      form.setFieldsValue({
        availablePrices: Array.isArray(prices) ? prices : [],
        extOptions: normalizedExts,
      })
      // Debug: log the fetched extensions and the value set on the form
      try {
        // eslint-disable-next-line no-console
        console.debug('[applySiesaItemToForm] fetched extensions:', normalizedExts)
        // eslint-disable-next-line no-console
        console.debug('[applySiesaItemToForm] form extOptions now:', form.getFieldValue('extOptions'))
      } catch (e) {}
    } catch (e) {}

    // Dispatch DOM events as an additional channel (some components listen to these)
    try {
      window?.dispatchEvent?.(new CustomEvent('siesa:availablePrices', { detail: { reference, prices } }))
    } catch (e) {}
    try {
      window?.dispatchEvent?.(new CustomEvent('siesa:extensions', { detail: { reference, extensions: exts } }))
    } catch (e) {}
  } catch (err) {
    // swallow errors to avoid breaking UI; callers may show messages
    // but keep console for debugging
    // eslint-disable-next-line no-console
    console.error('[applySiesaItemToForm] error', err)
  }
}

export default applySiesaItemToForm
